import React, { useEffect, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { generatePath, useHistory } from 'react-router-dom';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import RoutesMap from './Components/RoutesMap';
import routes from '../../routes';
import { Colors } from '../../assets/theme/Colors';
import { MARKER_DONOR } from '../Map/Components/MarkersSelector';
import StopDetails from './Components/StopDetails';
import RouteSchedule from './Components/RouteSchedule';
import { fetchRescueSizesIfNeeded } from '../../actions/rescueSizes';
import NewRouteReview from './Components/NewRouteReview';

const RoutesStopDetails = ({ handleCancelNewRoute, stops = [], defaultValues = {}, distanceAndDuration = {} }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const rescueSizesEntities = useSelector((state) => state.entities.rescueSizes);
  const rescueSizes = rescueSizesEntities.sorted;
  const [activeStep, setActiveStep] = useState(0);
  const [scheduleStep, setScheduleStep] = useState(false);
  const [reviewStep, setReviewStep] = useState(false);
  const [stopDetails, setStopDetails] = useState(defaultValues);
  const [scheduleDetails, setScheduleDetails] = useState({
    frequency: 0,
    date: moment().format('YYYY-MM-DD'),
    start_date: moment().format('YYYY-MM-DD'),
    end_date: null,
    pause_date: null,
    resume_date: null,
    weekly_days: [],
    monthly_days: [],
    rescue_size: {},
    rescuer_notes: '',
  });
  const currentStop = stops[activeStep];
  const label = currentStop?.title || currentStop?.name || currentStop?.address;
  const isDonor = currentStop?.type === MARKER_DONOR;

  useEffect(() => {
    dispatch(fetchRescueSizesIfNeeded());
  }, []);

  const getStepColor = (index) => {
    if (index === activeStep) {
      return Colors.appBar.main;
    }

    if (index <= activeStep) {
      return '#f0a790';
    }

    return '#E0E0E0';
  };

  const updatePickupBeginTime = ({ event }) => {
    setStopDetails((prev) => ({
      ...prev,
      [currentStop.id]: {
        ...prev[currentStop.id],
        pickup_begin: moment(event).format('HH:mm:ss'),
      },
    }));
  };

  const updatePickupEndTime = ({ event }) => {
    setStopDetails((prev) => ({
      ...prev,
      [currentStop.id]: {
        ...prev[currentStop.id],
        pickup_end: moment(event).format('HH:mm:ss'),
      },
    }));
  };

  const updateWeight = ({ target }) => {
    setStopDetails((prev) => ({
      ...prev,
      [currentStop.id]: {
        ...prev[currentStop.id],
        weight: target.value,
      },
    }));
  };

  const updateCategories = (type) => {
    const categoryFound = [...stopDetails[currentStop.id].categories].find((category) => category === type);
    setStopDetails((prev) => ({
      ...prev,
      [currentStop.id]: {
        ...prev[currentStop.id],
        categories: categoryFound
          ? [...prev[currentStop.id].categories].filter((category) => category !== type)
          : [...prev[currentStop.id].categories, type],
      },
    }));
  };

  const updateFrequency = (frequency) => {
    setScheduleDetails((prev) => ({ ...prev, frequency }));
  };

  const updateWeeklyDays = (day) => {
    const dayFound = (scheduleDetails?.weekly_days || []).find((d) => d === day);
    setScheduleDetails((prev) => ({
      ...prev,
      weekly_days: dayFound ? prev.weekly_days.filter((d) => d !== day) : [...prev.weekly_days, day],
    }));
  };

  const updateMonthlyOrdinal = (ordinal) => {
    const ordinalFound = (scheduleDetails?.monthly_days || []).find((o) => o.ordinal === ordinal);
    setScheduleDetails((prev) => ({
      ...prev,
      monthly_days: ordinalFound
        ? prev.monthly_days.filter((o) => o.ordinal !== ordinalFound.ordinal)
        : [...prev.monthly_days, { ordinal, days: [] }],
    }));
  };

  const updateMonthlyDays = (ordinal, day) => {
    const ordinalIndex = (scheduleDetails?.monthly_days || []).findIndex((o) => o.ordinal === ordinal);
    const dayFound = (scheduleDetails?.monthly_days?.[ordinalIndex]?.days || []).find((d) => d === day);

    if (ordinalIndex > -1) {
      setScheduleDetails((prev) => ({
        ...prev,
        monthly_days: prev.monthly_days.map((md) => {
          if (md.ordinal === ordinal) {
            return {
              ordinal,
              days: dayFound
                ? prev.monthly_days[ordinalIndex].days.filter((d) => d !== day)
                : [...prev.monthly_days[ordinalIndex].days, day],
            };
          }

          return md;
        }),
      }));
    }
  };

  const updateDate = (date) => {
    setScheduleDetails((prev) => ({ ...prev, date }));
  };

  const updateStartDate = (date) => {
    setScheduleDetails((prev) => ({ ...prev, start_date: date }));
  };

  const updateEndDate = (date) => {
    setScheduleDetails((prev) => ({ ...prev, end_date: date }));
  };

  const updatePauseDate = (date) => {
    setScheduleDetails((prev) => ({ ...prev, pause_date: date }));
  };

  const updateResumeDate = (date) => {
    setScheduleDetails((prev) => ({ ...prev, resume_date: date }));
  };

  const updateRescueSize = (size) => {
    setScheduleDetails((prev) => ({ ...prev, rescue_size: size }));
  };

  const updateRescuerNotes = (value) => {
    setScheduleDetails((prev) => ({ ...prev, rescuer_notes: value }));
  };

  const handleBack = () => {
    setActiveStep(0);
    setScheduleStep(false);
    setReviewStep(false);
    history.push(generatePath(routes.routes));
  };

  const handlePrevious = () => {
    if (reviewStep) {
      return setReviewStep(false);
    }

    if (scheduleStep) {
      return setScheduleStep(false);
    }
    return setActiveStep((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const handleNext = () => {
    if (scheduleStep) {
      return setReviewStep(true);
    }

    if (activeStep + 1 === stops.length) {
      return setScheduleStep(true);
    }
    return setActiveStep((prev) => prev + 1);
  };

  const renderNextLabel = () => {
    if (scheduleStep) {
      return 'Continue to Review';
    }

    if (activeStep + 1 === stops.length) {
      return 'Continue to Schedule';
    }

    return 'Next Step';
  };

  const renderBackLabel = () => {
    if (scheduleStep) {
      return 'Back';
    }

    return 'Previous';
  };

  if (!stops.length) {
    return history.push(generatePath(routes.routes));
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 'calc(100vh - 90px)',
        maxHeight: 'calc(100vh - 90px)',
        display: 'flex',
        overflowY: 'auto',
        flexDirection: reviewStep ? 'row' : 'column',
      }}
    >
      <RoutesMap
        styles={{
          height: reviewStep ? '100%' : '30vh',
          width: '100%',
          flexShrink: reviewStep ? 1 : 0,
          position: 'relative',
        }}
        stopDetailsStep
        reviewStep={reviewStep}
        selectedLocations={stops}
      />

      {!reviewStep && (
        <>
          <Paper elevation={25} sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '800px' }}>
              <Button
                startIcon={<ArrowBack />}
                size="small"
                variant="text"
                disableRipple
                onClick={handleBack}
                sx={{
                  color: 'GrayText',
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Edit Stops
              </Button>

              {scheduleStep ? (
                <Typography variant="body1" sx={{ color: 'GrayText', fontWeight: 700 }}>
                  Schedule
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, width: '50%' }}>
                  {stops.map((stop, index) => (
                    <Box
                      key={stop.id}
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: getStepColor(index),
                        transition: 'background-color 0.2s ease',
                      }}
                    />
                  ))}
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {scheduleStep ? 'Final Step' : `Stop ${activeStep + 1} of ${stops.length}`}
              </Typography>
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <Box sx={{ width: '800px' }}>
              {!scheduleStep && (
                <StopDetails
                  stopDetails={stopDetails?.[currentStop.id]}
                  updateCategories={updateCategories}
                  updateWeight={updateWeight}
                  updatePickupBeginTime={updatePickupBeginTime}
                  updatePickupEndTime={updatePickupEndTime}
                  isDonor={isDonor}
                  label={label}
                  activeStep={activeStep}
                />
              )}

              {scheduleStep && (
                <RouteSchedule
                  frequency={scheduleDetails?.frequency}
                  weeklyDays={scheduleDetails?.weekly_days}
                  monthlyDays={scheduleDetails?.monthly_days}
                  date={scheduleDetails?.date}
                  startDate={scheduleDetails?.start_date}
                  endDate={scheduleDetails?.end_date}
                  pauseDate={scheduleDetails?.pause_date}
                  resumeDate={scheduleDetails?.resume_date}
                  rescueSize={scheduleDetails.rescue_size}
                  rescuerNotes={scheduleDetails.rescuer_notes}
                  rescueSizes={rescueSizes}
                  onFrequencyChange={updateFrequency}
                  onWeeklyDayChange={updateWeeklyDays}
                  onMonthlyOrdinalChange={updateMonthlyOrdinal}
                  onMonthlyDayChange={updateMonthlyDays}
                  onDateChange={updateDate}
                  onStartDateChange={updateStartDate}
                  onEndDateChange={updateEndDate}
                  onPauseDateChange={updatePauseDate}
                  onResumeDateChange={updateResumeDate}
                  onRescueSizeChange={updateRescueSize}
                  onRescuerNotesChange={updateRescuerNotes}
                />
              )}
            </Box>
          </Box>

          <Paper elevation={25} sx={{ p: 2, display: 'flex', justifyContent: 'center', mt: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '800px' }}>
              <Button
                variant="contained"
                startIcon={<ArrowBack />}
                disabled={activeStep === 0}
                sx={{
                  textTransform: 'none',
                  bgcolor: Colors.white,
                  color: 'text.primary',
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: Colors.white,
                  },
                }}
                onClick={handlePrevious}
              >
                {renderBackLabel()}
              </Button>

              {!scheduleStep && (
                <Typography variant="body1" color="text.disabled">
                  {label}
                </Typography>
              )}

              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                sx={{
                  textTransform: 'none',
                  bgcolor: Colors.appBar.main,
                  color: Colors.white,
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: Colors.appBar.light,
                  },
                }}
                onClick={handleNext}
              >
                {renderNextLabel()}
              </Button>
            </Box>
          </Paper>
        </>
      )}

      {reviewStep && (
        <NewRouteReview
          handleCancelNewRoute={handleCancelNewRoute}
          handlePrevious={handlePrevious}
          stops={stops}
          stopDetails={stopDetails}
          scheduleDetails={scheduleDetails}
          distanceAndDuration={distanceAndDuration}
        />
      )}
    </Box>
  );
};

export default RoutesStopDetails;
